import React, { useEffect, useMemo, useState } from "react";
import SimpleSpinner from "../loader";
import { AnalyticsService, EntityType, FilterEntityEnum, ReportTypesEnum } from "@propertelligent/client-api";
import { infiniteScrollHook } from "../../hooks/infiniteScrollHook";
import apiHelper from "../../utils/apiHelper";
import EmptyState from "../../newComponents/emptystate";
import AddButtonWithArrow from "../firstTimeUserExperience/commonCom/AddButtonWithArrow";
import { GetImages } from "../../utils/GetImages";
import ActionMenu from "../../newComponents/actionMenus/action-menu";
import { useLocation, useNavigate } from "react-router-dom";
import CommonTwoPaneView from "./component/CommonTwoPaneView/CommonTwoPaneView";
import useInfiniteScroll from "react-infinite-scroll-hook";
import DateHelper from "../../utils/DateHelper";
import { Offcanvas } from "react-bootstrap";
import { usePermissions } from "../../hooks/usePermissions";
import { PermissionGuard } from "../../newComponents/PermissionGuard";
import NoAccess from "../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { useGeneratePdfContext } from "../../utils/context/GeneratePdfContext";
import { Range } from "react-date-range";
import FiltersForm from "./Reports/FiltersForm";
import { ConfirmationPopup } from "../../newComponents/confirmation-popup/confirmation-popup-demo";
import { UserLoginData } from "../common/charts/Enums";
import ReportStatement from "./Reports/ReportStatement";
import NotesIcon from "../../assets/notesFileIcon.svg";

const getMiddayUTCString = (date: Date | any) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0)).toISOString();
};

const Reports = () => {
  const navigate = useNavigate();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportId, setReportId] = useState();
  const [filterDirty, setFilterDirty] = useState(false);
  const [filterCloseRequestToken, setFilterCloseRequestToken] = useState(0);
  const [activeTab, setActiveTab] = useState("Pnl");
  const [PnlSortDirection, setPnlSortDirection] = useState("asc");
  const [RentRollSortDirection, setRentRollSortDirection] = useState("asc");
  const [showStatement, setShowStatement] = useState(false);
  const [showButton, setShowButton] = useState(false)
  const reportTypeName = activeTab === "Pnl" ? `profit-loss-${DateHelper.getCurrentDateTime()}` : `rent-roll-${DateHelper.getCurrentDateTime()}`
  const [hasAnyReports, setHasAnyReports] = useState<boolean>(false);
  const location = useLocation();
  const { selectedIds, setSelectedIds, selectedValues, setSelectedValues, selectedOption, setSelectedOption, statementData, setStatementData } = useGeneratePdfContext();
  const [relativeDateString, setRelativeDateString] = useState("")
  const [reportName, setReportName] = useState("")

  const check = usePermissions();
  const canWrite = check.canWrite("Reports");

  const [range, setRange] = useState<Range[]>([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  const resetForm = () => {
    setSelectedOption(FilterEntityEnum.Organization)
    setSelectedValues([])
    setSelectedIds([])
    setRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
      }
    ])
    setRelativeDateString("")
    setReportId(null)
    setReportName("")
  }

  const reportType = parseInt(localStorage.getItem("ReportType"))


  // ------------------ PNL REPORTS ------------------
  const {
    rows,
    loading,
    rowCount: rowCount,
    hasMore,
    fetchData,
    currentPage,
    handleSort,
    currentSortDirection
  } = infiniteScrollHook({
    httpFetchDataFn: AnalyticsService.getApiReportOrganizationlist,
    FullyQualifiedFilters: `reportTypeId == ${ReportTypesEnum.Pnl}`,
    FullyQualifiedSorts: `name ${PnlSortDirection}`,
  });

  const loadMorePNL = () => {
    if (!loading && hasMore) fetchData(currentPage + 1);
  };
  useMemo(() => { setPnlSortDirection(currentSortDirection) }, [currentSortDirection])
  const [pnlRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMorePNL,
    disabled: !hasMore,
    rootMargin: "0px 0px 250px 0px",
  });

  // ------------------ RENTROLL REPORTS ------------------
  const {
    rows: rows1,
    loading: loading1,
    rowCount: rowCount1,
    hasMore: hasMore1,
    fetchData: fetchData1,
    currentPage: currentPage1,
    handleSort: handleSort1,
    currentSortDirection: currentSortDirection1
  } = infiniteScrollHook({
    httpFetchDataFn: AnalyticsService.getApiReportOrganizationlist,
    FullyQualifiedFilters: `reportTypeId == ${ReportTypesEnum.RentRoll}`,
    FullyQualifiedSorts: `name ${RentRollSortDirection}`,
  });

  const loadMoreRentRoll = () => {
    if (!loading1 && hasMore1) fetchData1(currentPage1 + 1);
  };

  const [rentRollRef] = useInfiniteScroll({
    loading: loading1,
    hasNextPage: hasMore1,
    onLoadMore: loadMoreRentRoll,
    disabled: !hasMore1,
    rootMargin: "0px 0px 250px 0px",
  });

  useMemo(() => { setRentRollSortDirection(currentSortDirection1) }, [currentSortDirection1])

  // ------------------ DELETE CONFIRM ------------------
  const handleDelete = () => {
    if (isDeleting) return;

    setIsDeleting(true);
    apiHelper(
      AnalyticsService.deleteApiReport,
      {
        showNotification: true,
        title: "Report Deleted",
        successMessage: "Report Deleted Successfully",
        failureMessage: "Unable to Delete Report",
      },
      reportId
    ).then((res) => {
      if (res?.data?.success) {
        setShowConfirmation(false);
        fetchData(1);
        fetchData1(1);
      }
    }).finally(() => {
      setIsDeleting(false);
    });
  };

  useEffect(() => {
    resetForm();
  }, []);

  useEffect(() => {
    if (rowCount > 0 || rowCount1 > 0) {
      setHasAnyReports(true);
    }
  }, [rowCount, rowCount1]);

  // ------------------ RENDER EMPTY ------------------
  const renderEmptyState = (label) => (
    <EmptyState
      heading={hasAnyReports ? `No ${label} Reports Found` : `Add Your First ${label} Report`}
      actionNode={
        !hasAnyReports && canWrite ? (
          <AddButtonWithArrow onClick={() => {
            resetForm();
            setShowReportForm(true);
            setReportId(null)
            const reportType = activeTab === "Pnl"
              ? ReportTypesEnum.Pnl
              : ReportTypesEnum.RentRoll;
            localStorage.setItem("ReportType", String(reportType));
          }} buttonname="Add Report" />
        ) : null
      }
      icon={NotesIcon}
      isBorder={false}
    />
  );

  // ------------------ RENDER REPORT LIST ------------------
  const renderReportsList = (rows, label, loading, hasMore, observerRef, sortFn) => {
    if ((!rows || rows.length === 0) && !loading) return renderEmptyState(label);

    if ((!rows || rows.length === 0) && loading) {
      return (
        <div className="d-flex justify-content-center align-items-center h-100 w-100" style={{ minHeight: '300px' }}>
             <SimpleSpinner myClassName="Icon50" />
        </div>
      )
    }

    return (
      <div className="report-list">
        {rows?.map((item) => (
          <div key={item.id}
            className="report-item flex-column">
            <div className="report-info cursor-pointer" onClick={() => {
              setShowButton(true)
              setShowStatement(true); setReportId(item?.id);
              setReportName(item.name)
              const reportType = activeTab === "Pnl"
                ? ReportTypesEnum.Pnl
                : ReportTypesEnum.RentRoll;

              localStorage.setItem("ReportType", String(reportType));
            }}>
              <div className="report-item-header">{item?.name}</div>
              <div className="report-item-description">
                Duration: {
                  item?.queryData?.dateFilter?.formatted?.DateFilterTypeId !== "Custom"
                    ? item?.queryData?.dateFilter?.formatted?.DateFilterTypeId
                    : item?.queryData?.dateFilter?.formatted?.DateFilterTypeId === "Custom"
                      ? `From ${DateHelper.convertUTCtoDateTime(item?.queryData?.dateFilter?.startDate)}
           to ${DateHelper.convertUTCtoDateTime(item?.queryData?.dateFilter?.endDate)}`
                      : ""
                }
              </div>
            </div>
            <div className="separator-horizontal" />
            {/* ACTION MENU */}
            <div className="d-flex justify-content-between">
              <div className="d-flex flex-wrap align-items-center gap-2">
                {/* Filter Type */}
                <span>
                  {item?.queryData?.formatted?.FilterEntityTypeId}
                  {item?.queryData?.formatted?.FilterEntityTypeId === "organization" && " :"}
                </span>

                {/* Units */}
                {item?.queryData?.entityNames
                  ?.slice(0, 3)
                  .map((u, i) => (
                    <span key={i} className="pill" title={u}>{u}</span>
                  ))}

                {/* +More */}
                {item?.queryData?.entityNames?.length > 3 && (
                  <span className="pill">+ {item.queryData.entityNames.length - 3} More</span>
                )}

              </div>
              {canWrite && (
                <ActionMenu
                  menuItems={[
                    {
                      label: "Edit",
                      icon: GetImages.BlackPencil,
                      action: () => {
                        setShowReportForm(true);
                        setReportId(item.id);
                        setReportName(item.name);
                        const rpType = activeTab === "Pnl" ? ReportTypesEnum.Pnl : ReportTypesEnum.RentRoll;
                        localStorage.setItem("ReportType", String(rpType));
                      },
                    },
                    { isDivider: true },
                    {
                      label: "Delete",
                      icon: GetImages.BlackDelete,
                      action: () => {
                        setReportId(item.id);
                        setShowConfirmation(true);
                      },
                    },
                  ]}
                  trigger={
                    <button className="action-button">
                      <img src={GetImages.dropdownIcon} alt="menu" width={20} className="iconStyleWithWhiteFilter" />
                    </button>
                  }
                />
              )}
            </div>
          </div>
        ))}

        {/* INFINITE SCROLL TRIGGER */}
        {(loading || hasMore) && (
          <div ref={observerRef} className="loading-trigger">
            {loading && <SimpleSpinner myClassName="Icon40" />}
          </div>
        )}
      </div>
    );
  };

  const organizationIdFromSession = sessionStorage.getItem(UserLoginData.organization_id);
  const organizationId = organizationIdFromSession ? parseInt(organizationIdFromSession, 10) : null;


  const createStatementData = (formValues) => {
    const dateFilterTypeId = DateHelper.getDateFilterType(formValues.relativeDateString);
    if (Number(formValues.selectedOption) === FilterEntityEnum.Organization) {
      if (!organizationId || isNaN(organizationId) || organizationId <= 0) {
        console.error("Organization id missing or invalid while creating statement:", organizationId);
        return Promise.reject(new Error("Organization id missing or invalid"));
      }
    }

    const statementObject = {
      filterEntityTypeId: Number(formValues.selectedOption),
      filterEntityIds: Number(formValues.selectedOption) === FilterEntityEnum.Organization
        ? [organizationId]
        : (formValues.selectedIds || []),
      entityNames: Number(formValues.selectedOption) === FilterEntityEnum.Organization
        ? []
        : (formValues.selectedValues || []),
      dateFilter: {
        dateFilterTypeId,
        clientDate: DateHelper.getClientDate(),
        startDate: (formValues.relativeDateString === "Custom" || formValues.relativeDateString === "Custom Range") ? getMiddayUTCString(formValues.range[0].startDate) : null,
        endDate: (formValues.relativeDateString === "Custom" || formValues.relativeDateString === "Custom Range") ? getMiddayUTCString(formValues.range[0].endDate) : null
      }
    };

    if (reportType === ReportTypesEnum.Pnl) {
      return apiHelper(AnalyticsService.postApiReportPnl, { showNotification: false }, statementObject);
    } else {
      return apiHelper(AnalyticsService.postApiReportRentRoll, { showNotification: false }, statementObject);
    }
  };
  const updateReport = async (formValues, reportId) => {
    const organizationIdFromSession = sessionStorage.getItem(UserLoginData.organization_id);
    const orgId = organizationIdFromSession ? parseInt(organizationIdFromSession, 10) : null;

    const dateFilterTypeId = DateHelper.getDateFilterType(formValues.relativeDateString);

    const isCustom =
      formValues.relativeDateString === "Custom" ||
      formValues.relativeDateString === "Custom Range";

    const reportObj = {
      id: reportId,
      entityId: orgId,
      entityTypeId: EntityType.Organization,
      name: reportName,
      description: "",
      reportTypeId: reportType,
      queryData: {
        filterEntityTypeId: Number(formValues.selectedOption),
        filterEntityIds:
          Number(formValues.selectedOption) === FilterEntityEnum.Organization
            ? [orgId]
            : formValues.selectedIds,
        entityNames:
          Number(formValues.selectedOption) === FilterEntityEnum.Organization
            ? []
            : formValues.selectedValues,
        dateFilter: {
          dateFilterTypeId,
          clientDate: DateHelper.getClientDate(),
          startDate: isCustom ? getMiddayUTCString(formValues.range[0].startDate) : null,
          endDate: isCustom ? getMiddayUTCString(formValues.range[0].endDate) : null,
        },
      },
    };

    try {
      await apiHelper(
        AnalyticsService.putApiReport,
        {
          showNotification: true,
          successMessage: "Report Updated Successfully",
          failureMessage: "Unable to Update Report",
        },
        reportId,
        reportObj
      );

      // 🔁 refresh list after update
      fetchData(1);
      fetchData1(1);
    } catch (err) {
      console.error("Update failed:", err);
    }
  };
  const handleViewReport = async (formValues, reportId) => {

    try {
      const response = await createStatementData(formValues);
      if (response?.data?.success) {
        setStatementData(response.data.success);

        if (reportId) {
          await updateReport(formValues, reportId);
          if (activeTab === "Pnl") {
            fetchData(1);
          } else {
            fetchData1(1);
          }
        }

        setShowReportForm(false);
        setShowStatement(true);
      } else {
        console.error("Failed to generate statement:", response);
      }
    } catch (err) {
      console.error("Could not generate statement:", err);

    }
  };
  const Goback = () => {
    setShowStatement(false)
    setSelectedOption(FilterEntityEnum.Organization)
    setSelectedValues([])
    setSelectedIds([])
    setRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
      }
    ])
    setRelativeDateString("")
    setReportName("")
    setReportId(null)
    setShowButton(false)

    if (activeTab === "Pnl") {
      fetchData(1)
    } else {
      fetchData1(1)
    }

  }
  return (
    <PermissionGuard permission="Reports" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <>
        {showStatement ? (
          <ReportStatement
            //  id={reportId ? reportId : undefined} 
            showButton={showButton}
            reportId={reportId ? reportId : undefined}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            range={range}
            setRange={setRange}
            relativeDateString={relativeDateString}
            setRelativeDateString={setRelativeDateString}
            setReportName={setReportName}
            reportName={reportName}
            Goback={Goback}
            setShowReportForm={setShowReportForm}
            setReportId={setReportId}
          />
        ) : (

          <CommonTwoPaneView
            title="Reports"
            sidebarTabs={[
              { key: "Pnl", label: "Profit & Loss", count: rowCount },
              { key: "RentRoll", label: "Rent Roll", count: rowCount1 },
            ]}
            handleSort={handleSort || handleSort1}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            addButtonLabel="Add Report"
            onAdd={canWrite ? () => {
              resetForm();
              setShowReportForm(true);
              setReportId(null)
              const reportType = activeTab === "Pnl"
                ? ReportTypesEnum.Pnl
                : ReportTypesEnum.RentRoll;  // make sure this matches your enum name

              localStorage.setItem("ReportType", String(reportType));
            } : undefined}
            currentSortDirection={currentSortDirection || currentSortDirection1}
            loadingCombined={loading || loading1}
            hasAnyReports={hasAnyReports}
            renderListForTab={(tabKey) =>
              tabKey === "Pnl"
                ? renderReportsList(rows, "Profit & Loss", loading, hasMore, pnlRef, handleSort)
                : renderReportsList(rows1, "Rent Roll", loading1, hasMore1, rentRollRef, handleSort1)
            }
          />)}
        <ConfirmationPopup
          isOpen={showConfirmation}
          title="Delete Report"
          message="Do you want to delete this Report?"
          primaryButtonLabel="Delete"
          secondaryButtonLabel="Cancel"
          onPrimaryAction={handleDelete}
          onSecondaryAction={() => {
            setShowConfirmation(false);
          }}
          onClose={() => {
            setShowConfirmation(false);
          }}
          isPrimaryDestructive={true}
          isLoading={isDeleting}
          loadingLabel="Deleting..."
        />
        <Offcanvas
          className="offcanvas serviceRequestForm"
          show={showReportForm}
          onHide={() => {
            if (filterDirty) {
              setFilterCloseRequestToken((t) => t + 1);
              return;
            }
            setShowReportForm(false);
          }}
          placement={"end"}
          scroll={true} >
          <Offcanvas.Header className="canvasHeader" closeButton>
            <Offcanvas.Title>Add Report </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <div className='Template_View_Main reportFilters'>
              <div className="application-section">
                <FiltersForm
                  selectedOption={selectedOption}
                  setSelectedOption={setSelectedOption}
                  selectedValues={selectedValues}
                  setSelectedValues={setSelectedValues}
                  range={range}
                  onHide={() => {
                    setFilterDirty(false);
                    setShowReportForm(false);
                  }}
                  setRange={setRange}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                  relativeDateString={relativeDateString}
                  setRelativeDateString={setRelativeDateString}
                  id={reportId ? reportId : null}
                  onViewReport={(formValues) => handleViewReport(formValues, reportId)}
                  onDirtyChange={setFilterDirty}
                  closeRequestToken={filterCloseRequestToken}
                />
              </div>
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      </>
    </PermissionGuard>
  );
};

export default Reports;